import { useState, useEffect } from 'react';
import { usePageTitle } from '../appInfo';
import { Link } from 'react-router-dom';
import { api } from '../api/api';
import LiveServerPanel from '../components/LiveServerPanel';
import WorkersPanel from '../components/WorkersPanel';
import FieldGenWorkerPanel from '../components/FieldGenWorkerPanel';
import GameServerPanel from '../components/GameServerPanel';
import OneDrivePanel from '../components/OneDrivePanel';
import { formatServerTime } from '../utils/serverTime';

const sections = [
  { to: '/duels', label: 'デュエル一覧', desc: 'デュエルの作成・管理・ダウンロード' },
  { to: '/decks', label: 'デッキ管理',   desc: 'デッキの追加・閲覧・カード画像設定' },
  { to: '/cards', label: 'カード管理',   desc: 'スキャンしたカード画像のアップロード' },
];

function ServerStatus() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(false);
  const [durationInput, setDurationInput] = useState('120');
  const [saving, setSaving] = useState(false);
  const [sleeping, setSleeping] = useState(false);

  // 状態確認は status アクションのみを使う。wake は停止中のインスタンスを
  // 起動させてしまうため、単なる表示更新のためには呼ばない。
  const refreshStatus = async () => {
    try {
      const res = await api.getServerStatus();
      setInfo(res);
      setError(false);
    } catch {
      setError(true);
    }
  };

  useEffect(() => { refreshStatus(); }, []);

  const handleSetDuration = async () => {
    const minutes = parseInt(durationInput, 10);
    if (!minutes || minutes <= 0) return;
    setSaving(true);
    try {
      const res = await api.wake(minutes);
      setInfo(res);
      setError(false);
    } catch {
      setError(true);
    }
    setSaving(false);
  };

  const handleSleep = async () => {
    setSleeping(true);
    try {
      const res = await api.sleepServer();
      setInfo(res);
      setError(false);
    } catch {
      setError(true);
    }
    setSleeping(false);
  };

  return (
    <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
          backgroundColor: error ? '#ccc' : info?.state === 'running' ? '#2ecc71' : '#f1c40f',
        }} />
        <span>
          {error
            ? 'サーバー状態を取得できませんでした'
            : !info
              ? '確認中…'
              : info.state === 'running' ? 'サーバー稼働中' : `サーバー状態: ${info.state}`}
        </span>
      </div>

      {info && !error && (
        <span style={{ fontSize: 12, color: '#888' }}>
          {info.scheduled_stop
            ? `稼働終了予定: ${formatServerTime(info.scheduled_stop)}`
            : `自動停止: ${info.default_stop ?? '22:00 (毎日)'}`}
        </span>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
        <input
          type="number"
          min="1"
          placeholder="稼働時間(分)"
          value={durationInput}
          onChange={e => setDurationInput(e.target.value)}
          style={{ width: 100 }}
        />
        <button type="button" onClick={handleSetDuration} className="btn" disabled={saving}>
          {saving ? '設定中...' : '延長'}
        </button>
        <button
          type="button"
          onClick={handleSleep}
          className="btn"
          disabled={sleeping || info?.state !== 'running'}
        >
          {sleeping ? '休止中...' : 'サーバーを休止'}
        </button>
      </div>
    </div>
  );
}

const INSTALL_CMD = 'pip install synchro-client --find-links https://www.ds.synchro-3384.com/pypi/synchro_client/index.html';

function InstallCommand() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(INSTALL_CMD);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // クリップボードAPIが使えない環境では無視
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title" style={{ marginBottom: 8 }}>synchro-client のインストール</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* 地の色だけ指定して字の色を書いていなかったので、body の #e0e0e0 を継いで
            **明るい地に明るい字**になっていた（ほぼ読めない）。暗い地に統一する。
            minWidth も 280 だと iPhone 縦でボタンを押し出すので 0 にし、
            コマンド自体は横スクロールさせる */}
        <code style={{
          flex: 1, minWidth: 0, padding: '6px 10px',
          background: '#0f3460', border: '1px solid #1a4080', color: '#cfe2ff',
          borderRadius: 4, fontSize: 13, overflowX: 'auto', whiteSpace: 'nowrap',
        }}>
          {INSTALL_CMD}
        </code>
        <button type="button" onClick={handleCopy} className="btn">
          {copied ? 'コピーしました' : 'コピー'}
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  usePageTitle('ダッシュボード');
  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">ダッシュボード</h1>
      </div>
      <ServerStatus />
      <LiveServerPanel />
      <GameServerPanel />
      <FieldGenWorkerPanel />
      <WorkersPanel />
      <OneDrivePanel />
      <InstallCommand />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
        {sections.map(s => (
          <Link key={s.to} to={s.to} style={{ textDecoration: 'none' }}>
            <div className="card" style={{ cursor: 'pointer' }}>
              <div className="card-title">{s.label}</div>
              <div className="card-meta">{s.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
